import { UnifiedAlarm } from '@hakimo-ui/hakimo/types';
import { useMemo } from 'react';
import { isBlacklisted } from '../../matched-faces/util';
const RUN_GAP_MS = 5000;

export interface FaceMarker {
  /** Absolute timestamp in ms on the timeline */
  absoluteTime: number;
  /** Matched profile, part of the marker key */
  profileId: string;
  /** Tooltip text for the marker */
  label: string;
  /** The alarm this marker belongs to */
  alarmId: string;
}

interface FaceDetection {
  absoluteTime: number;
  profileId: string;
  profileName: string;
}

function rgDetections(alarm: UnifiedAlarm): FaceDetection[] {
  const video = alarm.media?.video;
  const faces = video?.matchedFaces;
  if (!video || !faces || faces.length === 0) return [];

  const videoStartMs = video.startTime
    ? new Date(video.startTime).getTime()
    : Number.NaN;
  const startTime = Number.isNaN(videoStartMs)
    ? alarm.alarmStartTime
    : videoStartMs;

  return faces
    .filter(
      (face) =>
        typeof face.videoTimeSec === 'number' && isBlacklisted(face.profileType)
    )
    .map((face) => ({
      absoluteTime: startTime + (face.videoTimeSec as number) * 1000,
      profileId: face.profileId,
      profileName: face.profileName,
    }));
}

function scanDetections(alarm: UnifiedAlarm): FaceDetection[] {
  return (alarm.media?.frames ?? []).flatMap((frame) =>
    (frame.matched_faces ?? [])
      .filter((face) => isBlacklisted(face.profile_type))
      .map((face) => ({
        absoluteTime: frame.timestamp,
        profileId: face.profile_id,
        profileName: face.profile_name,
      }))
  );
}

// One marker per profile per contiguous run of detections.
function collapseRuns(
  detections: FaceDetection[],
  alarmId: string
): FaceMarker[] {
  const markers: FaceMarker[] = [];
  const lastSeen = new Map<string, number>();

  const sorted = [...detections].sort(
    (a, b) => a.absoluteTime - b.absoluteTime
  );
  for (const detection of sorted) {
    const previous = lastSeen.get(detection.profileId);
    lastSeen.set(detection.profileId, detection.absoluteTime);
    if (
      previous !== undefined &&
      detection.absoluteTime - previous <= RUN_GAP_MS
    ) {
      continue;
    }

    markers.push({
      absoluteTime: detection.absoluteTime,
      profileId: detection.profileId,
      label: detection.profileName
        ? `Face detected: ${detection.profileName}`
        : 'Face detected',
      alarmId,
    });
  }

  return markers;
}

export function useFaceMarkers(alarms: UnifiedAlarm[]): FaceMarker[] {
  return useMemo(
    () =>
      alarms.flatMap((alarm) =>
        collapseRuns(
          [...rgDetections(alarm), ...scanDetections(alarm)],
          alarm.id
        )
      ),
    [alarms]
  );
}
